import { useState } from 'react';

import { Check, Copy, ExternalLink, GitBranch, GitCommit, Tag, ChevronDown, ChevronUp } from 'lucide-react';

import { formatDateTime, formatRelativeTime } from '@/lib/utils';

interface GitCommitInfoProps {
    commitMessage: string;
    commitHash: string;
    branch?: string;
    date: string;
    commitUrl: string;
    releaseVersion?: string;
    releaseDate?: string;
}

export function GitCommitInfo({
    commitMessage,
    commitHash,
    branch = 'main',
    date,
    commitUrl,
    releaseVersion,
    releaseDate,
}: GitCommitInfoProps) {
    const [copied, setCopied] = useState(false);
    const [showCommitDetails, setShowCommitDetails] = useState(false);
    const shortHash = commitHash.slice(0, 7);

    const copyHash = () => {
        navigator.clipboard.writeText(commitHash);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Calculate time since release (or commit date if release date is not available)
    const displayDate = releaseDate || date;
    let timeSinceRelease = '';
    let formattedReleaseDate = '';
    if (displayDate) {
        timeSinceRelease = formatRelativeTime(displayDate);
        formattedReleaseDate = formatDateTime(displayDate);
    }

    return (
        <div className="w-full rounded-md border border-border bg-card overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-muted/50 border-b border-border">
                <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold text-foreground">
                        Latest Release
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <GitBranch className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-mono text-muted-foreground">
                        {branch}
                    </span>
                </div>
            </div>

            {/* Main Release Info */}
            <div className="p-4 border-b border-border/50">
                <div className="flex items-start gap-3 justify-between">
                    <div className="flex-1 min-w-0">
                        {releaseVersion ? (
                            <div className="flex items-center gap-2">
                                <span className="text-xl font-bold text-foreground tracking-tight">
                                    {releaseVersion}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                    Active
                                </span>
                            </div>
                        ) : (
                            <span className="text-base font-semibold text-foreground">
                                No tag found (dev build)
                            </span>
                        )}
                        <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                            Deployed {timeSinceRelease}
                            {formattedReleaseDate && (
                                <span className="text-xs text-muted-foreground/60">
                                    ({formattedReleaseDate})
                                </span>
                            )}
                        </p>
                    </div>

                    <button
                        onClick={() => setShowCommitDetails(!showCommitDetails)}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 py-1 px-2 rounded hover:bg-muted/50 transition-colors"
                    >
                        {showCommitDetails ? 'Hide details' : 'Show details'}
                        {showCommitDetails ? (
                            <ChevronUp className="h-3 w-3" />
                        ) : (
                            <ChevronDown className="h-3 w-3" />
                        )}
                    </button>
                </div>
            </div>

            {/* Expandable/Subtle Commit Details (Hidden/Less Prominent) */}
            {showCommitDetails && (
                <div className="p-4 bg-muted/20 border-t border-border/30 transition-all">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-muted-foreground w-20">
                                Message:
                            </span>
                            <span className="text-sm text-foreground truncate max-w-md">
                                {commitMessage}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-muted-foreground w-20">
                                Commit SHA:
                            </span>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={copyHash}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted hover:bg-muted/80 transition-colors group"
                                >
                                    <GitCommit className="h-3 w-3 text-muted-foreground" />
                                    <code className="text-xs font-mono text-primary">
                                        {shortHash}
                                    </code>
                                    {copied ? (
                                        <Check className="h-3 w-3 text-primary" />
                                    ) : (
                                        <Copy className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-muted-foreground w-20">
                                Date:
                            </span>
                            <span className="text-xs text-muted-foreground">
                                {date}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-muted-foreground w-20">
                                Link:
                            </span>
                            <a
                                href={commitUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                            >
                                View commit
                                <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
